import React, {useState} from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import {toast} from "react-toastify";
import {faIcons} from "../../../utils/iconConstants";
import {toastSuccess, toastWarning} from "../../../utils/toast";
import SelectDropdown from "../../Components/Dropdowns/SelectDropdown";
import {toUniverseOption, universeLabel} from "./storeUniverseTableHelpers";
import {
    OVERRIDABLE_FIELDS,
    OVERRIDE_CLASSIFICATION,
    OVERRIDE_CLOSED_DATE,
    OVERRIDE_OPEN_DATE,
    OVERRIDE_UNIVERSE_ADD,
    OVERRIDE_UNIVERSE_REMOVE,
    overrideValues,
    saveCanonicalStoreOverride,
    UNIVERSE_OVERRIDE_FIELDS,
} from "../canonicalStoreOverrides";

export const CLOSED_BEFORE_OPEN_ERROR = 'Closed date must be after open date';
export const NO_SOURCE_DATE = 'None';

const DATE_FIELDS = [
    {field: OVERRIDE_OPEN_DATE, label: 'Open Date'},
    {field: OVERRIDE_CLOSED_DATE, label: 'Closed Date'},
];

const CLASSIFICATION_OPTIONS = [
    {label: 'INDIE', value: 'indie'},
    {label: 'CHAIN', value: 'chain'},
];

const UNIVERSE_FIELDS = [
    {field: OVERRIDE_UNIVERSE_ADD, otherField: OVERRIDE_UNIVERSE_REMOVE, label: 'Add to Universes'},
    {field: OVERRIDE_UNIVERSE_REMOVE, otherField: OVERRIDE_UNIVERSE_ADD, label: 'Remove from Universes'},
];

const sameUniverses = (universes, otherUniverses) =>
    universes.length === otherUniverses.length && universes.every(universe => otherUniverses.includes(universe));

const isUnchanged = (values, initialValues) =>
    OVERRIDABLE_FIELDS.every(field => values[field] === initialValues[field])
    && UNIVERSE_OVERRIDE_FIELDS.every(field => sameUniverses(values[field], initialValues[field]));

const universeLabels = (universes) => universes.map(universeLabel).join(', ');

const selectableUniverseOptions = (universeOptions, selected, excluded) => [
    ...universeOptions,
    ...selected.filter(universe => !universeOptions.some(({value}) => value === universe)).map(toUniverseOption),
].filter(({value}) => !excluded.includes(value));

const CanonicalStoreOverrideModal = ({version, store, override, universeOptions = [], onClose, onSaved}) => {
    const initialValues = overrideValues(override);
    const [values, setValues] = useState(initialValues);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const closedBeforeOpen = !!values[OVERRIDE_OPEN_DATE] && !!values[OVERRIDE_CLOSED_DATE]
        && values[OVERRIDE_OPEN_DATE] >= values[OVERRIDE_CLOSED_DATE];
    const canSubmit = !saving && !closedBeforeOpen && !isUnchanged(values, initialValues);

    const onSubmit = () => {
        setSaving(true);
        setSaveError(null);
        return saveCanonicalStoreOverride(version, store.canonicalStoreId, values)
            .then(response => {
                toastSuccess(toast, `Saved the overrides for ${store.canonicalStoreId}`);
                response.warnings.forEach(warning => toastWarning(toast, warning.message, {autoClose: 3000}));
                onSaved(response);
            })
            .catch(error => {
                setSaveError(error.message);
                setSaving(false);
            });
    };

    return (
        <Modal
            isOpen={true}
            className="Modal store-override-modal"
            overlayClassName="Overlay"
            ariaHideApp={false}
            shouldCloseOnOverlayClick={false}
            onRequestClose={() => !saving && onClose()}
            testId={"store-override-modal"}
        >
            <h5 className={"modal-header-text"}>Override Canonical Store Values</h5>
            <div className={"store-override-store"} data-testid={"store-override-store"}>
                <div className={"store-info-field"}>
                    <div className={"store-info-field-label"}>Canonical Store</div>
                    <div className={"store-info-field-value"} data-testid={"store-override-canonical-store-id"}>{store.canonicalStoreId}</div>
                </div>
                <div className={"store-info-field"}>
                    <div className={"store-info-field-label"}>Store</div>
                    <div className={"store-info-field-value"} data-testid={"store-override-store-name"}>{store.storeName || store.bannerName}</div>
                </div>
            </div>
            <div className={"callout primary small store-override-help"}>
                <i className={faIcons.infoCircle} aria-hidden={true}/>
                Leave a field blank to use the value from the store sources.
            </div>
            <div className={"store-override-fields"} data-testid={"store-override-fields"}>
                {DATE_FIELDS.map(({field, label}) => (
                    <div className={"store-info-field store-override-field"} key={field}>
                        <label className={"store-info-field-label"} htmlFor={`store-override-${field}`}>{label}</label>
                        <input
                            id={`store-override-${field}`}
                            type={"date"}
                            value={values[field]}
                            disabled={saving}
                            onChange={e => setValues(previous => ({...previous, [field]: e.target.value}))}
                        />
                        <div className={"store-override-current"} data-testid={`store-override-current-${field}`}>
                            Current: {store[field] || NO_SOURCE_DATE}
                        </div>
                    </div>
                ))}
                <div className={"store-info-field store-override-field"}>
                    <label className={"store-info-field-label"} htmlFor={`store-override-${OVERRIDE_CLASSIFICATION}`}>Classification</label>
                    <SelectDropdown
                        id={`store-override-${OVERRIDE_CLASSIFICATION}_dropdown`}
                        inputId={`store-override-${OVERRIDE_CLASSIFICATION}`}
                        containerClassName={"store-override-classification"}
                        dataTestId={"store-override-classification-dropdown"}
                        options={CLASSIFICATION_OPTIONS}
                        selectValue={values[OVERRIDE_CLASSIFICATION] || null}
                        selectLabel={CLASSIFICATION_OPTIONS.find(({value}) => value === values[OVERRIDE_CLASSIFICATION])?.label}
                        isClearable
                        placeholder={'No Override'}
                        isDisabled={saving}
                        onChange={selection => setValues(previous => ({...previous, [OVERRIDE_CLASSIFICATION]: selection?.value ?? ''}))}
                    />
                    <div className={"store-override-source-values"}>
                        <div className={"store-override-current"} data-testid={`store-override-current-${OVERRIDE_CLASSIFICATION}`}>
                            Current: {store[OVERRIDE_CLASSIFICATION] || NO_SOURCE_DATE}
                        </div>
                        <div className={"store-override-current"} data-testid={"store-override-recommended-classification"}>
                            Recommended: {store.recommendedClassification || NO_SOURCE_DATE}
                        </div>
                    </div>
                </div>
            </div>
            <div className={"store-override-fields"} data-testid={"store-override-universe-fields"}>
                {UNIVERSE_FIELDS.map(({field, otherField, label}) => (
                    <div className={"store-info-field store-override-field"} key={field}>
                        <label className={"store-info-field-label"} htmlFor={`store-override-${field}`}>{label}</label>
                        <SelectDropdown
                            id={`store-override-${field}_dropdown`}
                            inputId={`store-override-${field}`}
                            containerClassName={"store-override-universes"}
                            dataTestId={`store-override-${field}-dropdown`}
                            isMulti
                            isClearable
                            placeholder={'No Override'}
                            options={selectableUniverseOptions(universeOptions, values[field], values[otherField])}
                            selectValues={values[field]}
                            isDisabled={saving}
                            menuPortalTarget={document.body}
                            styles={{menuPortal: (base) => ({...base, zIndex: 9999})}}
                            onChange={selections => setValues(previous => ({
                                ...previous,
                                [field]: selections?.map(({value}) => value) ?? [],
                            }))}
                        />
                    </div>
                ))}
            </div>
            <div className={"store-override-source-values"}>
                <div className={"store-override-current"} data-testid={"store-override-current-universes"}>
                    Current: {universeLabels(store.universe ?? []) || NO_SOURCE_DATE}
                </div>
                <div className={"store-override-current"} data-testid={"store-override-recommended-universes"}>
                    Recommended: {universeLabels(store.recommendedUniverseValues) || NO_SOURCE_DATE}
                </div>
            </div>
            {closedBeforeOpen && <div className={'error'} role={'alert'}>{CLOSED_BEFORE_OPEN_ERROR}</div>}
            {saveError && <div className={'error'} role={'alert'} data-testid={"store-override-save-error"}>{saveError}</div>}
            <div className={"right-things"}>
                <button type="button" className="button modal-button cancel" name="cancel"
                        onClick={onClose} disabled={saving}>
                    Cancel
                </button>
                <button type="button" className={"button modal-button confirm"} name="submit"
                        onClick={onSubmit} disabled={!canSubmit}>
                    {saving
                        ? <><i className={`${faIcons.spinner} fa-spin`} data-testid={"spinner"}/><span>Saving</span></>
                        : "Submit"}
                </button>
            </div>
        </Modal>
    );
};

CanonicalStoreOverrideModal.propTypes = {
    version: PropTypes.string.isRequired,
    store: PropTypes.shape({
        canonicalStoreId: PropTypes.string.isRequired,
        storeName: PropTypes.string,
        bannerName: PropTypes.string,
        openDate: PropTypes.string,
        closedDate: PropTypes.string,
        classification: PropTypes.string,
        recommendedClassification: PropTypes.string,
        universe: PropTypes.arrayOf(PropTypes.string),
        recommendedUniverseValues: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    override: PropTypes.shape({
        openDate: PropTypes.string,
        closedDate: PropTypes.string,
        classification: PropTypes.string,
        overrideUniverseAdd: PropTypes.arrayOf(PropTypes.string),
        overrideUniverseRemove: PropTypes.arrayOf(PropTypes.string),
    }),
    universeOptions: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.string,
    })),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

export default CanonicalStoreOverrideModal;
